type Nest = {
  start: number; // absolute to the start of the string
  end: number; // absolute to the start of the string
  hasChildren: boolean;
  children: CMxArtifacts.UnstructuredSpan[];
  parent: CMxArtifacts.UnstructuredSpan;
};

// ---------------------------------------
// Functions
// ---------------------------------------
/**
 * Comparitor function for span sorting.
 *
 * Almost all of the code for displaying highlighting relies on spans on a sorted list.  This function is left
 * exported so that it can be tested.
 *
 * @param spanA
 * @param spanB
 */
export const buildSorter = () => {
  return (
    spanA: CMxArtifacts.UnstructuredSpan,
    spanB: CMxArtifacts.UnstructuredSpan
  ) => {
    const startsBefore = spanA.span.start < spanB.span.start;
    if (startsBefore) {
      return -1;
    }

    const startsAfter = spanA.span.start > spanB.span.start;
    if (startsAfter) {
      return 1;
    }
    // both spans start at the same point.  we don't need to do
    // any fancy addition here, we can just compare offsets.

    const endsBefore = spanA.span.end < spanB.span.end;
    if (endsBefore) {
      return -1;
    }

    const endsAfter = spanA.span.end > spanB.span.end;
    if (endsAfter) {
      return 1;
    }
    // a and b both start and end in the same block.
    return 0;
  };
};

export const getActiveSpans = (
  spans: CMxArtifacts.UnstructuredSpan[],
  segment: CMxAPI.ContentSegment
) => {
  const tempSpans = [...spans];
  const activeSpans: CMxArtifacts.UnstructuredSpan[] = [];
  let accumulating = true;

  while (accumulating) {
    const activeSpan = tempSpans.shift();
    if (activeSpan === undefined) {
      accumulating = false;
    } else {
      const start = activeSpan?.span.start ?? undefined;
      if (start === undefined || isNaN(start) || start < segment.start) {
        // TODO: We should do something here.
      } else {
        // test to see if the span is still within the bounds
        // @ts-ignore
        const spanEnd = activeSpan.span.end;
        const segmentEnd = segment.end;
        if (spanEnd > segmentEnd) {
          accumulating = false;
        } else {
          activeSpans.push(activeSpan);
        }
      }
    }
  }

  return activeSpans;
};

/**
 * Determine if span2 is contained within span1.  Used to build nesting relationships.
 * @param span1
 * @param span2
 */
const isBoundBy = (
  span1: CMxArtifacts.UnstructuredSpan,
  span2: CMxArtifacts.UnstructuredSpan
) => {
  const span1End = span1.span.end;
  const startsWithin =
    span2.span.start >= span1.span.start && span2.span.start <= span1End;

  if (!startsWithin) return false;

  const span2End = span2.span.end;
  const endsWithin = span2End <= span1End;
  return endsWithin;
};

/**
 * Predicate function to determine if span2 overlaps span1.
 * @param span1
 * @param span2
 */
const isOverlap = (
  span1: CMxArtifacts.UnstructuredSpan,
  span2: CMxArtifacts.UnstructuredSpan
) => {
  const span1end = span1.span.end;

  // not contained within the span.
  if (span2.span.start >= span1end) {
    return false;
  }
  // test to see if the second span spills over the first.
  const span2end = span2.span.end;
  if (span2end > span1end) {
    return true;
  }
  // the span2 is contained within
  return false;
};

/**
 * Utility function to help make spans for normalizeOverlaps
 * @param span
 * @param mixin
 */
const buildOverlapFragment = (
  span: CMxArtifacts.UnstructuredSpan,
  mixin: Partial<CMxAPI.Span>
) => {
  return {
    ...span,
    span: {
      ...span.span,
      ...mixin
    }
  };
};

/**
 * Spans may cross over each other.
 * Ex: The quick brown fox jumped over the lazy dog.
 * Span 1: The quick brown (icd)
 * Span 2: brown fox jumped (cpt)
 *
 * The following example would not reflect what we want to happen.
 * Ex: <span class="icd">the quick <span class="cpt">browm</span> fox jumped</span>
 * The first closing span tag would close the cpt class.
 *
 * This function will generate code like the following:
 * <span class="icd">the quick </span><span class="icd"><span class="cpt">browm</span></span><span class="cpt"> fox jumped</span>
 * @param origSpans
 */
const normalizeOverlaps = (origSpans: CMxArtifacts.UnstructuredSpan[]) => {
  let currentIndex = 0;
  let spans = [...origSpans];

  while (currentIndex < spans.length - 1) {
    const activeSpan = spans[currentIndex];
    const nextSpan = spans[currentIndex + 1];
    const overlap = isOverlap(activeSpan, nextSpan);

    if (!overlap) {
      currentIndex++;
    } else {
      // detect the bounds of the overlap
      const overlapStart = nextSpan.span.start;
      const overlapEnd = activeSpan.span.end;
      // create 4 new spans to replace the two
      // * the first one will be the span before there's an overlap
      // * the second is the second half of the first span, ie. the overlapped content of the first span
      // * the third is the overlapped content of the second span
      // * finally, the unoverlapped content of the second span
      const preOverlap = buildOverlapFragment(activeSpan, {
        end: overlapStart
      });
      const overlap1 = buildOverlapFragment(activeSpan, {
        start: overlapStart,
        end: activeSpan.span.end
      });
      const overlap2 = buildOverlapFragment(nextSpan, {
        start: overlapStart,
        end: overlapEnd
      });
      const postOverlap = buildOverlapFragment(nextSpan, {
        start: overlapEnd,
        end: nextSpan.span.end
      });

      const min = Math.max(currentIndex, 0);
      const pre = spans.slice(0, min);
      const post = spans.slice(currentIndex + 2);
      spans = [...pre, preOverlap, overlap1, overlap2, postOverlap, ...post];
      currentIndex += 3;
    }
  }
  return spans;
};

/**
 * Utility function to determine
 * @param spans
 * @param segment
 */
export const buildNest = (
  spans: CMxArtifacts.UnstructuredSpan[],
  segment: CMxAPI.ContentSegment
): Nest => {
  const span = spans[0];
  const forwardSiblings = spans.slice(1);
  const start = span.span.start - segment.start;
  const end = span.span.end - segment.start;

  let nest: Nest = {
    hasChildren: false,
    start,
    end,
    children: [],
    parent: span
  };
  for (var j = 0; j < forwardSiblings.length; j++) {
    const sibling = forwardSiblings[j];
    if (isBoundBy(span, sibling)) {
      nest.hasChildren = true;
      nest.children.push(sibling);
    } else {
      // since we've already taken care of overlaps further
      // upstream we can safely exit from our tests at this point.
      break;
    }
  }
  return nest;
};

/**
 * Predicate function to determine if a span starts in a segment
 * @param segment
 * @param span
 */
const startsIn = (
  segment: CMxAPI.ContentSegment,
  span: CMxArtifacts.UnstructuredSpan
) => {
  const segmentEnd = segment.end;
  return (
    segment.end > 0 &&
    span.span.start >= segment.start &&
    span.span.start < segmentEnd
  );
};

/**
 * Predicate function to tell if a span is beyond a segment.  Does not
 * consider whether the span is contained within the segment.
 * @param segment
 * @param span
 */
const exceedsSegment = (
  segment: CMxAPI.ContentSegment,
  span: CMxArtifacts.UnstructuredSpan
) => {
  const spanEnd = span.span.end;
  const segmentEnd = segment.end;
  return spanEnd > segmentEnd;
};

/**
 * Predicate function to determine if a span is contained in and
 * exceeds a segment.
 * @param segment
 * @param span
 */
const crossesSegment = (
  segment: CMxAPI.ContentSegment,
  span: CMxArtifacts.UnstructuredSpan
) => {
  const contains = startsIn(segment, span);
  return contains && exceedsSegment(segment, span);
};

/**
 * This function returns two spans, one that terminates within segment1 another that begins with segment 2
 * @param segment1
 * @param segment2
 * @param span
 */
const buildSegmentCrossingSpans = (
  segment1: CMxAPI.ContentSegment,
  segment2: CMxAPI.ContentSegment,
  span: CMxArtifacts.UnstructuredSpan
) => {
  const segment1end = segment1.end;

  const span1 = {
    ...span,
    span: {
      ...span.span,
      end: segment1end
    }
  };

  const span2 = {
    ...span,
    span: {
      ...span.span,
      start: segment2.start,
      end: span.span.end
    }
  };
  return [span1, span2];
};

/**
 * Inserts a span into the list at the first point where the segment no longer references spans.
 * @param origSpans - A list of spans
 * @param spanToInsert - A new span to insert into the list
 * @param externalIndex - A pointer to the current position in the span list to start consideration for insertion.
 */
const insertSpan = (
  origSpans: CMxArtifacts.UnstructuredSpan[],
  spanToInsert: CMxArtifacts.UnstructuredSpan,
  externalIndex: number
) => {
  let currentIndex = externalIndex;
  let spans = [...origSpans];

  while (
    currentIndex < spans.length &&
    spans[currentIndex].span.start <= spanToInsert.span.start
  ) {
    currentIndex++;
  }
  const pre = spans.slice(0, currentIndex);
  const post = spans.slice(currentIndex);
  spans = [...pre, spanToInsert, ...post];

  return spans;
};

/**
 * Spans may cross segment boundaries.  This function ensures that spans are contained within segment boudaries.
 * @param segments
 * @param origSpans
 */
export const bindSpans = (
  segments: CMxAPI.ContentSegment[],
  origSpans: CMxArtifacts.UnstructuredSpan[]
) => {
  let spans = [...origSpans];
  let currentIndex = 0;
  for (var i = 0; i < segments.length; i++) {
    const segment = segments[i];

    // the next segment may not be a content segment, meaning there's
    // no point in creating a span within it's bounds.
    let finding = true;
    let j = i;
    let nextSegment;
    while (finding) {
      if (!segments[j + 1]) {
        finding = false;
      } else {
        nextSegment = segments[j + 1];
        if (nextSegment.end > 0) {
          finding = false;
        }
        j++;
      }
    }

    if (nextSegment) {
      while (
        segment &&
        currentIndex < spans.length &&
        startsIn(segment, spans[currentIndex])
      ) {
        const crossesBoundary = crossesSegment(segment, spans[currentIndex]);
        if (crossesBoundary) {
          const [currentSpan, spanToInsert] = buildSegmentCrossingSpans(
            segment,
            nextSegment,
            spans[currentIndex]
          );
          // replace the current span
          const pre = spans.slice(0, currentIndex);
          const post = spans.slice(currentIndex + 1);
          spans = [...pre, currentSpan, ...post];
          // insert the new span at the front of the line for the next segment
          spans = insertSpan(spans, spanToInsert, currentIndex);
        }
        currentIndex++;
      }
    }
  }
  return spans;
};

const isContent = (item: any): item is CMxCommonApp.Content => {
  return `content` in item;
};

const isNote = (item: any): item is CMxCommonApp.DecoratedNote => {
  return `segments` in item;
};

const segmentizeText = (text: string) => {
  const lines = `${text}`.split(/\n|\r/);

  let index = 0;
  return lines.map(line => {
    const segment = {
      start: index,
      end: index + line.length,
      text: line
    };
    index = index + line.length + 1;
    return segment;
  });
};

/**
 * Utility function to change a string or a content object that doesn't have
 * segments into a segment based content object.
 * @param text
 */
export const segmentize = (
  text:
    | CMxCommonApp.Content
    | string
    | CMxCommonApp.UnstructuredHighlightedContent
) => {
  if (typeof text === 'string') {
    return segmentizeText(text);
  } else if (isNote(text)) {
    return text.segments;
  } else if (isContent(text)) {
    return segmentizeText(text.content);
  } else {
    const content = !text ? '' : text + '';
    return [
      {
        start: 0,
        end: content.length,
        text: content
      }
    ];
  }
};

export const normalizeSpans = (
  segments: CMxAPI.ContentSegment[],
  spans: CMxArtifacts.UnstructuredSpan[]
) => {
  const boundedSpans = bindSpans(segments, spans);
  const normalized = normalizeOverlaps(boundedSpans);
  return normalized;
};
